//	SVG path builders. Coordinates are rounded to 0.5px to keep SSR markup compact.

const r = (v) => Math.round(v * 2) / 2;

//	points: ({x, y} | null)[]; a null breaks the line (data gap)
export function linePath(points) {
	let d = "";
	let pen = false;
	for (const p of points) {
		if (!p) {
			pen = false;
			continue;
		}
		d += (pen ? "L" : "M") + r(p.x) + " " + r(p.y);
		pen = true;
	}
	return d;
}

//	closed area per contiguous segment, down to the baseline
export function areaPath(points, baselineY) {
	let d = "";
	let segment = [];
	const flush = () => {
		if (segment.length < 1) return;
		const first = segment[0];
		const last = segment[segment.length - 1];
		d += "M" + r(first.x) + " " + r(baselineY);
		for (const p of segment) d += "L" + r(p.x) + " " + r(p.y);
		d += "L" + r(last.x) + " " + r(baselineY) + "Z";
		segment = [];
	};
	for (const p of points) {
		if (!p) flush();
		else segment.push(p);
	}
	flush();
	return d;
}

//	rect with the two corners on `side` rounded by `radius` (the data end), square elsewhere
export function roundedBar(x, y, w, h, radius, side) {
	if (w <= 0 || h <= 0) return "";
	const rad = Math.max(0, Math.min(radius, side === "top" || side === "bottom" ? Math.min(w / 2, h) : Math.min(h / 2, w)));
	const x1 = r(x), y1 = r(y), x2 = r(x + w), y2 = r(y + h);
	if (rad === 0) return `M${x1} ${y1}H${x2}V${y2}H${x1}Z`;
	const a = (ex, ey) => `A${rad} ${rad} 0 0 1 ${r(ex)} ${r(ey)}`;
	switch (side) {
		case "top":
			return `M${x1} ${y2}V${r(y + rad)}${a(x + rad, y)}H${r(x + w - rad)}${a(x + w, y + rad)}V${y2}Z`;
		case "bottom":
			return `M${x1} ${y1}H${x2}V${r(y + h - rad)}${a(x + w - rad, y + h)}H${r(x + rad)}${a(x, y + h - rad)}Z`;
		case "right":
			return `M${x1} ${y1}H${r(x + w - rad)}${a(x + w, y + rad)}V${r(y + h - rad)}${a(x + w - rad, y + h)}H${x1}Z`;
		case "left":
			return `M${x2} ${y1}V${y2}H${r(x + rad)}${a(x, y + h - rad)}V${r(y + rad)}${a(x + rad, y)}Z`;
		default:
			return `M${x1} ${y1}H${x2}V${y2}H${x1}Z`;
	}
}

//	annular sector from angle a0 to a1 (radians, 0 = 12 o'clock, clockwise); r0 = 0 → pie slice
export function arc(cx, cy, r0, r1, a0, a1) {
	const full = a1 - a0 >= Math.PI * 2 - 1e-6;
	if (full) a1 = a0 + Math.PI * 2 - 1e-4;
	const pt = (rad, a) => [r(cx + rad * Math.sin(a)), r(cy - rad * Math.cos(a))];
	const large = a1 - a0 > Math.PI ? 1 : 0;
	const [x1, y1] = pt(r1, a0);
	const [x2, y2] = pt(r1, a1);
	let d = `M${x1} ${y1}A${r1} ${r1} 0 ${large} 1 ${x2} ${y2}`;
	if (r0 > 0) {
		const [x3, y3] = pt(r0, a1);
		const [x4, y4] = pt(r0, a0);
		d += `L${x3} ${y3}A${r0} ${r0} 0 ${large} 0 ${x4} ${y4}Z`;
	} else {
		d += `L${r(cx)} ${r(cy)}Z`;
	}
	return d;
}
